import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
    Box,
    Card,
    CardContent,
    Avatar,
    Typography,
    CircularProgress,
    Pagination,
    Stack,
    Divider,
    IconButton,
    Tooltip,
    Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

const SearchHistoryUsersList = ({ companyId, pageSize = 50 }) => {
    const [page, setPage] = useState(1);
    const [users, setUsers] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async (signal) => {
        if (!companyId) return;
        setLoading(true);
        setError(null);

        try {
            const res = await axios.get(`https://api.sovrenn.com/search-history/users-list/${companyId}`, {
                headers: {
                    "Authorization": "Bearer " + localStorage.getItem("sov_user"),
                },
                params: {
                    page,
                    page_size: pageSize,
                },
                signal,
            });

            if (res.data?.success) {
                setUsers(res.data.data || []);
                setTotalUsers(res.data.pagination?.total_users || 0);
                setTotalPages(res.data.pagination?.total_pages || 1);
            } else {
                setError(res.data?.message || "Unexpected API response");
            }
        } catch (err) {
            if (!axios.isCancel(err)) {
                setError(err.response?.data?.message || err.message);
            }
        } finally {
            setLoading(false);
        }
    }, [companyId, page, pageSize]);

    useEffect(() => {
        const controller = new AbortController();
        fetchData(controller.signal);
        return () => controller.abort();
    }, [fetchData, page]);

    const formatDate = (iso) => {
        if (!iso) return "-";
        const d = new Date(iso);
        return d.toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const handleRefresh = () => setPage(1);
    const handlePageChange = (_, value) => setPage(value);

    return (
        <Box sx={{ mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                    Users who searched this company
                </Typography>

                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                        {totalUsers} user{totalUsers !== 1 ? "s" : ""}
                    </Typography>
                    <Tooltip title="Refresh">
                        <IconButton onClick={handleRefresh} size="small" disabled={loading}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                <CardContent sx={{ p: 0 }}>
                    {loading ? (
                        <Stack alignItems="center" justifyContent="center" py={6}>
                            <CircularProgress size={30} />
                            <Typography variant="body2" mt={1} color="text.secondary">
                                Loading...
                            </Typography>
                        </Stack>
                    ) : error ? (
                        <Alert severity="error" sx={{ m: 2 }}>
                            {error}
                        </Alert>
                    ) : users.length === 0 ? (
                        <Typography variant="body2" align="center" py={4} color="text.secondary">
                            No users found for this company.
                        </Typography>
                    ) : (
                        <>
                            {users.map((user, i) => (
                                <Box key={user.user_id}>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={2}
                                        sx={{ p: 2 }}
                                    >
                                        <Avatar
                                            src={user.profile_pic}
                                            alt={user.name}
                                            sx={{
                                                bgcolor: user.profile_pic ? "transparent" : "#b6d7f5",
                                                color: "#094067",
                                                width: 48,
                                                height: 48,
                                                fontWeight: 600,
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            {!user.profile_pic &&
                                                (user.name
                                                    ? user.name.split(" ").map((n) => n[0]).join("").slice(0, 2)
                                                    : "U")}
                                        </Avatar>

                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {user.name || "Unknown User"}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {user.email || user.phone_number || "-"}
                                            </Typography>
                                        </Box>

                                        <Box textAlign="right">
                                            <Typography variant="body1" fontWeight={700} color="primary">
                                                {user.searchCount}×
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(user.lastSearchedAt)}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    {i < users.length - 1 && <Divider />}
                                </Box>
                            ))}

                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                p={2}
                                borderTop="1px solid #eee"
                            >
                                <Typography variant="body2" color="text.secondary">
                                    Page {page} of {totalPages}
                                </Typography>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={handlePageChange}
                                    color="primary"
                                    size="small"
                                />
                            </Stack>
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

SearchHistoryUsersList.propTypes = {
    companyId: PropTypes.string.isRequired,
    pageSize: PropTypes.number,
};

export default SearchHistoryUsersList;
